/**
 * Skeleton fallback for Suspense boundaries.
 * - variant="full": full-page skeleton (initial route load, login, launcher).
 * - variant="content": content-area only (inside AppLayout; sidebar/header stay visible).
 * Uses stable layout and pulse animation to avoid flicker and layout shift.
 */
const SKELETON_CLASS =
  'rounded bg-slate-200/80 animate-pulse will-change-auto';

function SkeletonLine({ className = '' }) {
  return <div className={`h-4 ${SKELETON_CLASS} ${className}`.trim()} aria-hidden="true" />;
}

/**
 * Content-area skeleton: matches AppLayout content structure exactly so the
 * transition to real content has no flash (same card, background, elevation).
 * Mirrors: PageHeader (mb-6) + card (rounded-2xl border border-cream/60 bg-white shadow-sm) + p-8.
 */
function ContentSkeleton() {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col animate-fade-in-skeleton-content"
      role="status"
      aria-label="Loading content"
    >
      {/* Header: same spacing as PageHeader (mb-6) + breadcrumb-like line */}
      <header className="mb-6">
        <div className={`h-4 w-48 ${SKELETON_CLASS}`} aria-hidden="true" />
      </header>
      {/* Card: exact same elevation and background as real content (FinanceDashboard, MelDashboard, ListPageSection) */}
      <div className="min-h-0 flex-1 rounded-2xl border border-cream/60 bg-white shadow-sm">
        <div className="p-8">
          <div className="flex flex-col gap-2">
            <div className={`h-6 w-40 ${SKELETON_CLASS}`} aria-hidden="true" />
            <SkeletonLine className="h-4 w-64 max-w-[85%]" />
          </div>
          <div className="mt-6 space-y-3">
            <SkeletonLine className="w-full" />
            <SkeletonLine className="w-full" />
            <SkeletonLine className="w-[95%]" />
            <SkeletonLine className="w-[88%]" />
            <SkeletonLine className="w-[75%]" />
          </div>
        </div>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function FullPageSkeleton() {
  return (
    <div
      className="flex min-h-screen flex-col bg-slate-100 p-6 animate-fade-in-skeleton"
      role="status"
      aria-label="Loading application"
    >
      <div className="mx-auto w-full max-w-xl flex-1 flex flex-col justify-center gap-6 pt-12">
        <div className="flex flex-col gap-2">
          <SkeletonLine className="h-8 w-48" />
          <SkeletonLine className="h-4 w-72" />
        </div>
        <div className="space-y-3">
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-[90%]" />
          <SkeletonLine className="w-[85%]" />
          <SkeletonLine className="w-[75%]" />
        </div>
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export default function SuspenseFallback({ variant = 'full' }) {
  return variant === 'content' ? <ContentSkeleton /> : <FullPageSkeleton />;
}
